import React from "react";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import type { FormSuggestionState } from "../types";

import { QUESTION_TYPE_LABEL } from "../utils/question-type-labels";

type FormPreviewCardProps = {
	suggestionState: FormSuggestionState;
	onCreateForm: () => void;
	onDeleteForm: () => void;
	isCreating?: boolean;
};

export const FormPreviewCard: React.FC<FormPreviewCardProps> = ({
	suggestionState,
	onCreateForm,
	onDeleteForm,
	isCreating,
}) => {
	const { suggestion, status, createdFormBaseId } = suggestionState;

	if (status === "deleted") {
		return null;
	}

	const isCreated = status === "created";
	const questionCount = suggestion.questions.length;

	return (
		<div className="flex max-w-full min-w-0 flex-col gap-0">
			{/* Form card */}
			<div className="box-border flex max-w-full min-w-0 flex-col gap-3 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				{/* Header — name + question count */}
				<div className="flex flex-col gap-1">
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
						{suggestion.name}
					</Text>
					<Text size="caption" color="neutral-9">
						{questionCount} {questionCount === 1 ? "Question" : "Questions"}
					</Text>
				</div>

				<ol className="m-0 flex list-none flex-col gap-1 p-0">
					{suggestion.questions.map((q, idx) => (
						<li key={`${suggestion.id}-q-${idx}`} className="grid grid-cols-[1rem_1fr] gap-x-2">
							<Text size="caption" weight="semi-bold" color="neutral-9">
								{idx + 1}.
							</Text>
							<div className="flex flex-col gap-1">
								<Text size="caption" weight="semi-bold" color="neutral-9">
									{q.question}
									{q.is_required ? <span className="text-red-7"> *</span> : null}
								</Text>
								<Text size="extra-small" color="neutral-7">
									{QUESTION_TYPE_LABEL[q.type]}
								</Text>
							</div>
						</li>
					))}
				</ol>
			</div>

			{/* Action bar */}
			<div className="flex max-w-full min-w-0 items-center justify-between">
				<div className="shrink-0">
					{!isCreated && (
						<ButtonIcon
							icon={faTrashCan}
							onClick={onDeleteForm}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							aria-label="Delete form suggestion"
						/>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-1">
					{isCreated ? (
						<ButtonStandard
							variant="text"
							color="green"
							size="medium"
							iconLeft={faCircleCheck}
							onClick={() => {
								if (createdFormBaseId) {
									window.open(`/edit-review-form/${createdFormBaseId}`, "_blank");
								}
							}}
							dangerouslySetClassName="!w-auto"
						>
							View Form
						</ButtonStandard>
					) : (
						<ButtonStandard
							variant="text"
							color="primary"
							size="medium"
							iconLeft={faCirclePlus}
							onClick={onCreateForm}
							disabled={isCreating}
							dangerouslySetClassName="!w-auto"
						>
							{isCreating ? "Creating..." : "Create Form"}
						</ButtonStandard>
					)}
				</div>
			</div>
		</div>
	);
};

export default FormPreviewCard;
